import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SITE, ROUTE_SEO, CANONICAL_ALIASES, isPrivatePath, canonicalFor, normalizePath } from './seoConfig';
import { trackPageView } from './analytics';

let explicitPath = null;

const upsertMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!content) { el?.remove(); return; }
  if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute('content', content);
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement('link'); el.setAttribute('rel', rel); document.head.appendChild(el); }
  el.setAttribute('href', href);
};

const setJsonLd = (list) => {
  document.getElementById('ld-static')?.remove();
  document.head.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());
  (list || []).filter(Boolean).forEach((d, i) => {
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.dataset.seoJsonld = String(i);
    s.text = JSON.stringify(d);
    document.head.appendChild(s);
  });
};

export const applySeo = ({ title, description, canonical, robots, image, type, jsonLd }) => {
  const t = title || SITE.defaultTitle;
  const d = description || SITE.defaultDescription;
  const img = image || SITE.ogImage;
  document.title = t;
  upsertMeta('name', 'description', d);
  upsertMeta('name', 'robots', robots || 'index, follow');
  upsertLink('canonical', canonical);
  upsertMeta('property', 'og:title', t);
  upsertMeta('property', 'og:description', d);
  upsertMeta('property', 'og:url', canonical);
  upsertMeta('property', 'og:image', img);
  upsertMeta('property', 'og:image:secure_url', img);
  upsertMeta('property', 'og:type', type || 'website');
  upsertMeta('name', 'twitter:title', t);
  upsertMeta('name', 'twitter:description', d);
  upsertMeta('name', 'twitter:image', img);
  setJsonLd(jsonLd);
};

/** Page-level override. Numbers/strings only — pass `jsonLd` as an array of schema objects. */
export const SeoHead = ({ title, description, robots, image, type, canonical, jsonLd }) => {
  const { pathname } = useLocation();
  const ldKey = JSON.stringify(jsonLd || null);
  useEffect(() => {
    explicitPath = pathname;
    applySeo({ title, description, robots, image, type, canonical: canonical || canonicalFor(pathname), jsonLd });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, title, description, robots, image, type, canonical, ldKey]);
  return null;
};

/** Mounted once inside the Router: applies registry defaults + noindex for private routes, fires page views. */
export const RouteSeoDefaults = () => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (explicitPath !== pathname) {
      const entry = ROUTE_SEO[CANONICAL_ALIASES[normalizePath(pathname)] || normalizePath(pathname)];
      const priv = isPrivatePath(normalizePath(pathname));
      applySeo({
        title: entry?.title || SITE.defaultTitle,
        description: entry?.description,
        robots: priv || entry?.noindex ? 'noindex, nofollow' : 'index, follow',
        canonical: canonicalFor(pathname),
        jsonLd: entry?.jsonLd ? entry.jsonLd() : null,
      });
    }
    trackPageView(pathname + search);
  }, [pathname, search]);
  return null;
};
